import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  PenSquare,
  Sparkles,
  Users,
  BarChart3,
  CheckCircle,
  ArrowRight,
  Award,
  FileEdit,
  Send, // used in Vendor Responses card icon
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const SourcingLanding = () => {
  const navigate = useNavigate();

  const sourcingCards = [

    {
      id: 'rfps',
      title: 'RFP Registry',
      description:
        'Register, search, and process all sourcing requests from drafting to award.',
      icon: <ClipboardList size={24} />,
      path: '/sourcing/rfps',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'RFP table registry',
        'Stage-based lifecycle actions',
        'Search and filter controls',
      ],
    },
    {
      id: 'rfp-create-selection',
      title: 'Create RFP',
      description:
        'Create RFPs manually or via AI. Work on generated content in a unified workspace with real-time preview and section-wise editing.',
      icon: <PenSquare size={24} />,
      path: '/sourcing/rfps/create',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'AI-assisted generation',
        'Manual template editor',
        'RFP preview',
        'Section-wise editing',
        'Draft saving',
      ],
    },
    {
      id: 'rfp-workspace',
      title: 'RFP Workspace (Builder + Preview)',
      description:
        'Work on generated RFPs with real-time preview and section-wise editing, including draft saving and version control.',
      icon: <FileEdit size={24} />,
      path: '/sourcing/rfps/create/workspace',
      iconBg: '#F0F4FF',
      iconLine: '#4F46E5',
      features: [
        'RFP preview',
        'Section-wise editing',
        'Draft saving',
        'Version tracking',
        'Real-time updates',
      ],
    },
    {
      id: 'rfp-processing-master',
      title: 'RFP PROCESSING (MASTER)',
      description:
        'Lifecycle control center with stage-based actions, transitions and processing tabs.',
      icon: <ClipboardList size={24} />,
      path: '/sourcing/rfps/processing',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'Dynamic stage action bar',
        'Builder to Contract tabs',
        'Lifecycle transition enforcement',
      ],
    },
    {
      id: 'rfp-approval-screen',
      title: 'Approvals',
      description:
        'Review submitted RFPs and take Approve/Reject actions through the approval queue.',
      icon: <CheckCircle size={24} />,
      path: '/sourcing/rfps/approval',
      iconBg: '#E8FFF4',
      iconLine: '#0F766E',
      features: [
        'Queue-based approvals',
        'Approve or Reject actions',
        'Stage transition control',
      ],
    },
    {
      id: 'publish-vendor-mgmt',
      title: 'Publish & Vendor Management',
      description:
        'Manage RFP publishing and vendor interactions including invitations, deadlines, and submission tracking.',
      icon: <Users size={24} />,
      path: '/sourcing/publish-vendor-mgmt',
      iconBg: '#E6F6FF',
      iconLine: '#0369A1',
      features: [
        'Vendor invitation',
        'Deadline management',
        'Notification setup',
        'Submission tracking',
        'Attachment review',
      ],
    },
    {
      id: 'evaluation-award',
      title: 'Evaluation & Award',
      description:
        'Evaluate vendor submissions and finalize award decisions using technical, commercial, and weighted scoring methods.',
      icon: <Award size={24} />,
      path: '/sourcing/evaluation-award',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'Technical scoring',
        'Commercial comparison',
        'Weighted scoring',
        'Vendor selection',
        'Approval workflow',
      ],
    },
    {
      id: 'contract-conversion',
      title: 'CONTRACT CONVERSION SCREEN',
      description:
        'Preview sourcing-to-contract field mapping and create contract in CLM module.',
      icon: <ArrowRight size={24} />,
      path: '/sourcing/contracts/conversion',
      iconBg: '#E8F7FF',
      iconLine: '#0C4A6E',
      features: [
        'Mapping preview',
        'Create Contract action',
        'Push to CLM integration',
      ],
    },
    {
      id: 'reports-dashboard',
      title: 'Reports & Analytics',
      description:
        'Analyze sourcing KPIs including status mix, vendor performance, cycle time and approval delays.',
      icon: <BarChart3 size={24} />,
      path: '/sourcing/reports',
      iconBg: '#F0F9FF',
      iconLine: '#075985',
      features: [
        'RFP status summary cards',
        'Filter and export actions',
        'Cycle and approval metrics',
      ],
    },
    {
      id: 'template-master',
      title: 'TEMPLATE REGISTRY SCREEN',
      description:
        'Manage reusable RFP templates with placeholder mapping and lifecycle actions.',
      icon: <PenSquare size={24} />,
      path: '/sourcing/templates',
      iconBg: '#F5F3FF',
      iconLine: '#4C1D95',
      features: [
        'Template name/project type grid',
        'Create, Edit, Clone, Delete actions',
        'Placeholder system using {{}}',
      ],
    },
    {
      id: 'ai-prompt-config',
      title: 'AI PROMPT CONFIGURATION SCREEN',
      description:
        'Manage prompt name/content and section mappings, then activate one for runtime AI injection.',
      icon: <Sparkles size={24} />,
      path: '/sourcing/ai-prompts',
      iconBg: '#FFF4E6',
      iconLine: '#C2410C',
      features: [
        'Prompt Name and Prompt Content',
        'Create, Edit, Activate actions',
        'Dynamic LLM request injection',
      ],
    },
        {
      id: 'vendor-responses',
      title: 'Vendor Responses',
      description:
        'Browse published RFPs, submit proposals, and track the status of your bids from draft to approval.',
      icon: <Send size={24} />,
      path: '/sourcing/responses',
      iconBg: '#E8FFF4',
      iconLine: '#059669',
      features: [
        'Browse available RFPs',
        'Submit structured proposals',
        'Track Draft / Submitted / Approved status',
        'Edit draft responses',
      ],
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />

      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Strategic Sourcing"
        description="Manage the complete request-to-award lifecycle from vendor invitation to contract initiation."
      />

      <div className="page-container">
        {/* Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {sourcingCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%',
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px',
                    }}
                  >
                    <div
                      style={{ color: card.iconLine || 'var(--gold-primary)' }}
                      className="asset-card-icon"
                    >
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        margin: '0',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight size={20} className="asset-card-arrow" />
                </div>

                {/* Description */}
                <p
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    margin: '0 0 12px 0',
                    lineHeight: '1.4',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default SourcingLanding;
